/**
 * @author Angel Labrada
 * @since v0.0.1
 * @date 21/3/22
 */
import React, {memo, useState, useCallback, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Avatar, Collapse, Checkbox, Spin, Card, Button, Tooltip} from 'antd';
import classNames from 'classnames';
import {useParams} from 'react-router-dom';
import styles from '../TemplateDetailsFooterActions/index.less';
import FlagLineIcon from 'remixicon-react/FlagLineIcon';
import {useTranslation} from 'react-i18next';
import PicturesWall from '@/modules/templates/components/TemplateDetailsPicturesWall';
import {useCrudIncidentsRunAudit} from '@/modules/auditorAudits/hooks/useAuditorAudits';
import ContentEditableComponent from '@/components/ContentEditable';
import TaskComponent from './Task';
import {LoadingOutlined} from '@ant-design/icons';
import useNetwork from '@/hooks/useNetwork';
import {useAuditData} from '@/modules/auditorAudits/hooks/useAuditData';
import {mappedAuditDataIssue} from '@/utils/mappedAuditData';
import {ISSUE_RISK_LEVEL} from '@/modules/templates/contants/incidences';
import DraftLineIcon from 'remixicon-react/DraftLineIcon';
import {useDialogsContext} from '@/contexts/DialogsContext';
import {QUESTIONNAIRE_DIALOG} from '@/modules/templates/components/QuestionnaireDialog';
import useCrud from '@/hooks/useCrud';
import QuestionnaireApiService from '@/modules/templates/services/QuestionnaireApiService';
import {CACHE_KEY_AUDITOR_AUDITS} from '@/modules/auditorAudits/contants/auditorAudits';
import {useMutation, useQueryClient} from 'react-query';
import SectionsIEApiService from '@/modules/templates/services/SectionsIEApiService';
import {TermFilter} from '@dofleini/query-builder';
import {useSelectAuditView, useShowAuditView} from '@/modules/auditorAudits/hooks/useSelectAuditView';

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const { Panel } = Collapse;

const CheckIssuesFromRunAudit = ({item, runAudit, sectionId, question}) => {
  const {t} = useTranslation(('templates'));
  const {id} = useParams();
  const {dataSections, setDataSections} = useAuditData();
  const { openDialog } = useDialogsContext(QUESTIONNAIRE_DIALOG);
  const queryClient = useQueryClient();
  const { create: createQuestionnaire } = useCrud(QuestionnaireApiService, [
    CACHE_KEY_AUDITOR_AUDITS, 'get-one-questionnaire'
  ]);
  const {mutateAsync: getTemplateIESections} = useMutation(SectionsIEApiService.search);
  const {mutateAsync: getQuestionnaire} = useMutation(QuestionnaireApiService.getOne, {onSuccess: ({data}) => {
    setQuestionnaire(data);
    queryClient.invalidateQueries(['get-one-questionnaire', CACHE_KEY_AUDITOR_AUDITS]);
  }});
  const [activeKey, setActiveKey] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [isOpenQuestionnaire, setIsOpenQuestionnaire] = useState(false);

  const [checkedIssue, setCheckedIssue] = useState(runAudit ? question?.issues?.some(i => {
    if (i?._id === item?._id) {
      return i?.tasks?.some(t => t?.selected) || i?.selected;
    }
    return false;
  }) : false);
  const [checkedSIP, setCheckedSIP] = useState(runAudit ? question?.issues?.some(i => {
    if (i?._id === item?._id) {
      return i?.solvedInPlace;
    }
    return false;
  }) : false);
  const [checkedTask, setCheckedTask] = useState(runAudit ? question?.issues?.find(i => i?._id === item?._id)?.tasks?.map(t => t?.selected ? t?._id : null) || [] : []);
  const [checked, setChecked] = useState([]);

  const counterSelected = useMemo(() => {
    return question?.issues?.map(i => {
      if (i?._id === item?._id) {
        return i?.tasks?.find(t => t?.selected);
      }
      return false;
    })?.filter(x => x)?.length;
  }, [item?._id, question?.issues]);

  const {create, isLoading: isLoadingIncident, updateName, updateDescription, updateTaskName, solveInPlace} = useCrudIncidentsRunAudit();

  const {isActive, isIssue, isAnswer} = useShowAuditView(question?._id);
  const {updateCurrentView, issue} = useSelectAuditView();
    
  useEffect(() => {
    if(isActive && issue) {
      setActiveKey([issue?._id]);
    }
  }, [isActive, issue]);
  
  
  const handleChange = useCallback((e) => {
    updateCurrentView?.(sectionId, question?._id, 'incidences', item?._id);
    if(!isActive)
      return setActiveKey(e);
  }, [isActive, updateCurrentView, sectionId, question?._id, item]);
  
  const networkState = useNetwork(); // Connection status

  const onChange = useCallback((id) => {
    if (checked?.some(x => x === id)) setChecked(checked?.filter(x => x !== id));
    else setChecked([...checked, id]);
  }, [checked]);

  useEffect(() => {
    if (item?.tasks?.length === 1 && checked?.length === 0)
      onChange(item?.tasks?.[0]?._id);
    // eslint-disable-next-line
    }, []);

  const onSaveName = useCallback(async (data) => {
    if (runAudit) {
      if ('name' in data) {
        await updateName({auditId: id, sectionId, questionId: question?._id, name: data.name, issue: item?._id});
      } else {
        await updateDescription({auditId: id, sectionId, questionId: question?._id, description: data.description, issue: item?._id});
      }
    }
  },  [id, item?._id, question?._id, runAudit, sectionId, updateDescription, updateName]);

  const onSaveTaskName = useCallback(async (task, value) => {

    /*const issues = await question?.issues?.map((issue) => {
      if(issue?._id === item?._id){
        const tasks = issue?.tasks?.map(el => {
          if(el?._id === task?._id || el?._id === task?._id){
            return {...el, name: value};
          }
          return {...el};
        });
        return {...issue, tasks};
      }
      return issue;
    });*/

    if (runAudit) {
      await updateTaskName({auditId: id, questionId: question?._id, sectionId, name: value, task: task?._id, issue: item?._id});
    }
  },  [id, item?._id, question?._id, runAudit, sectionId, updateTaskName]);

  /** Handle check or uncheck SIP */
  const onCheckSIP = useCallback(async (evt) => {
    evt.stopPropagation();
    let checked = evt?.target?.checked;

    setCheckedSIP(checked);

    if (checked) {
      setActiveKey([item._id]);
    } else {
      setActiveKey([]);
    }

    /*if (checked && !checkedIssue) {
      await onCheckIssue(evt);
    }*/

    // just for run audit
    if(runAudit) {
      await solveInPlace({auditId: id, sectionId, questionId: question?._id, issue: item?._id, solvedInPlace: checked});
    }
  },  [id, item._id, question?._id, runAudit, sectionId, solveInPlace]);

  /** Handle check or uncheck issues */
  const onCheckIssue = useCallback(async (evt) => {
    evt.stopPropagation();
    let checked = evt?.target?.checked;

    setCheckedIssue(checked);

    if (checked) setActiveKey([item._id]);
    else setActiveKey([]);

    if (!checked && checkedSIP) {
      await onCheckSIP(evt);
    }

    // just for run audit
    if(runAudit) {
      const actions = checked ? {add: [{issue: item?._id, ...(item?.tasks?.length === 1 && {task : item?.tasks?.[0]?._id})}]} : {remove: [{issue: item?._id}]};

      const valuesForCheck = {
        auditId: id,
        sectionId,
        questionId: question?._id,
        ...actions
      };

      if (item?.tasks?.length === 1) {
        await create(valuesForCheck);
        setCheckedTask(prevState => (checked ? [...prevState, item?.tasks?.[0]?._id] : []));
        setChecked(prevState => (checked ? [...prevState, item?.tasks?.[0]?._id] : []));
      } else {
        await create(valuesForCheck);
        setCheckedTask(prevState => (checked ? [...prevState, item?._id] : []));
        setChecked(prevState => (checked ? [...prevState, item?._id] : []));
        setDataSections(mappedAuditDataIssue(dataSections, sectionId, question?._id, item?._id, {...item, selected: evt?.target?.checked}));
      }

      // Only apply when an incident has a single task marked
      if (actions?.remove?.length && counterSelected >= 0) {
        const actions = {remove: [{issue: item?._id}]};

        const values = {
          auditId: id,
          sectionId,
          questionId: question?._id,
          ...actions
        };
        await create(values);
      }
    }
  },  [checkedSIP, counterSelected, create, dataSections, id, item, onCheckSIP, question?._id, runAudit, sectionId, setDataSections]);

  const onCheckTask = useCallback(async (e, task) => {
    e.stopPropagation();

    let update = [];

    if (!e.target.checked) {
      update = checkedTask?.filter(x => x !== task?._id);
    } else {
      update = [...checkedTask, task?._id];
    }

    setCheckedTask(update);
    setChecked(update);

    if (update?.length > 0) setCheckedIssue(true);

    // just for run audit
    if(runAudit) {
      const actions = e.target.checked ? {add: [{issue: item?._id, task: task?._id}]} : {remove: [{issue: item?._id, task: task?._id}]};

      const values = {
        auditId: id,
        sectionId,
        questionId: question?._id,
        ...actions
      };
      await create(values);
    }
  },  [checkedTask, create, id, item?._id, question?._id, runAudit, sectionId]);

  const openQuestionnaire = useCallback(async (templateIssue) => {
    setIsOpenQuestionnaire(true);
    const questionIssue = question?.issues?.find(i => i?._id === templateIssue?._id);
    let existingQuestionnaire = null;
    let questionnaireCreated = null;
    const filters = new TermFilter({ field: 'template', value: templateIssue?.templateIE });
    if (!questionIssue?.questionnaire && !questionnaire) {
      const {data: ieSections} = await getTemplateIESections({filters, sort: {'settings.order': 1}});
      const questionsAnswers = [];
      ieSections.forEach((ieSection, idx) => {
        questionsAnswers.push({
          sectionTitle: ieSection.title,
          questionsAnswers: [],
        });
        ieSection.questions.forEach((question) => {
          questionsAnswers?.[idx]?.questionsAnswers.push({
            question: question.name || question.question,
            type: question?.text?.answerType || 'SIMPLE',
            answer: question?.text?.toFillByOthers ? question?.text?.label : '',
          });
        });
      });
      const {data: created} = await createQuestionnaire({
        auditDate: new Date(),
        audit: id,
        questionsAnswers,
        templateIssue: templateIssue?._id,
        language: templateIssue?.language,
        section: sectionId,
        question: question?._id,
        templateIE: templateIssue?.templateIE
      });
      questionnaireCreated = created;
      setQuestionnaire(questionnaireCreated);
    } else {
      if(questionIssue?.questionnaire && !questionnaire) {
        const {data: existing} = await getQuestionnaire({questionnaireId: questionIssue?.questionnaire});
        existingQuestionnaire = existing;
        setQuestionnaire(existingQuestionnaire);
      }
      if (questionnaire) {
        setQuestionnaire(questionnaire);
      }
    }
    openDialog(
      {
        templateIssue,
        audit: id,
        questionnaire: existingQuestionnaire || questionnaireCreated || questionnaire,
        sectionId,
        questionId: question?._id
      });
    setIsOpenQuestionnaire(false);
    setQuestionnaire(null);
  }, [createQuestionnaire, getQuestionnaire, getTemplateIESections, id, openDialog, question?._id, question?.issues, questionnaire, sectionId]);

  return (
    <div className={classNames(styles.headerCollapse, isAnswer && styles.disabledCheckbox)}>
      <Collapse
        bordered={true}
        key={item._id}
        forceRender
        activeKey={activeKey}
        onChange={handleChange}
        expandIconPosition='end'
        expandIcon={isIssue ? () => null : null}
      >
        <Panel
          header={
            <div className="h-full w-full flex min-h-6 flex-col md:flex-row">
              <div className="h-full w-full flex items-center justify-start gap-2">
                <Checkbox
                  checked={ checkedIssue ? 'checked' : null }
                  onClick={ !isAnswer ? onCheckIssue : undefined }
                  disabled={ isLoadingIncident }
                />
                <Avatar icon={ <FlagLineIcon size={ 17 } color="#707070" /> } className="min-w-8 flex items-center justify-center" />
                <Spin indicator={ antIcon } key={ item?._id } spinning={ networkState?.online && (isLoadingIncident) }>
                  <ContentEditableComponent
                    id={ item?._id }
                    initValue={ item?.name }
                    onSave={ (value) => onSaveName({ name: value }) }
                    className="max-w-3\/5"
                    isLoading={ isLoadingIncident }
                    disabled={ isAnswer }
                  />
                </Spin>
                {
                  item?.templateIE && item?.riskLevel === ISSUE_RISK_LEVEL.IE && checkedIssue &&
                  <div className="flex flex-col justify-center content-center items-center mr-1">
                    <Tooltip title={ t('questionnaireIE.fillQuestionnaire') } key={ 'fillQuestionnaire' }>
                      <Button type="text" icon={ <DraftLineIcon color={ '#7F7F7F' } /> } onClick={ () => openQuestionnaire(item) } loading={ isOpenQuestionnaire } />
                    </Tooltip>
                  </div>
                }
              </div>
              <div className="flex items-center justify-end ml-2">
                <Checkbox
                  checked={ checkedSIP ? 'checked' : null }
                  onClick={ !isAnswer ? onCheckSIP : undefined }
                  disabled={ !isAnswer && (isLoadingIncident || !checkedIssue) }
                />
                <span className="font-bold">{ t('templates.sip') }</span>
              </div>
            </div>
          }
          key={item._id}
        >
          <div className={classNames('w-full h-full', item?.deductionPercentage ? 'mb-2' : 'mb-4')}>
            <ContentEditableComponent
              id={`description-${item?._id}`}
              initValue={item?.description}
              onSave={(value) => onSaveName({description: value})}
              placeholder={t('templates.description')}
              isLoading={isLoadingIncident}
            />
          </div>

          {item?.deductionPercentage && <div className="w-full flex items-center justify-start mb-4">
            <div className="font-bold">{t('deductionPercentage')}:</div>
            <div className="ml-2">{item?.deductionPercentage}%</div>
          </div>}

          {checkedIssue && <div className="mt-4">
            <PicturesWall
              runAudit={runAudit}
              sectionId={sectionId}
              question={question}
              incidence={item}
              setCheckedIssue={setCheckedIssue}
              translation={'templates'}
              disabled={isLoadingIncident}
            />
          </div>}

          <Card
            className={'mb-2 rounded'}
            bodyStyle={{padding: '6px', background: '#FAFAFA'}}
          >
            <div className="font-bold my-2">{t('details.tasks')}:</div>
            {item?.tasks?.length > 0 && item?.tasks?.map((task, index) => {
              return (
                <TaskComponent
                  key={index}
                  item={task}
                  onChange={(e) => onCheckTask(e, task)}
                  checked={checkedTask?.includes(task?._id) ? 'checked' : null}
                  issuesAudit={question?.issuesAudit}
                  issueData={item}
                  onSaveTaskName={onSaveTaskName}
                  isLoading={isLoadingIncident}
                />
              );
            })}
          </Card>
        </Panel>
      </Collapse>
    </div>
  );
};

CheckIssuesFromRunAudit.propTypes = {
  item: PropTypes.object,
  i: PropTypes.number,
  handleOnClickTask: PropTypes.func,
  selectedTask: PropTypes.number,
  runAudit: PropTypes.bool,
  sectionId: PropTypes.string,
  question: PropTypes.object,
};

export default memo(CheckIssuesFromRunAudit);
